// src/utils/quote/quoteSession.js
//
// TWO ids live here, and mixing them up breaks a metric in each direction.
//
//   getSessionId() — one UUID per BROWSER, in localStorage, never rotated. It is
//     the *visitor* identity. usePresenceHeartbeat sends it as `anonId`, and
//     BetonijerryVisitorDaily counts distinct anonIds per day, so anything that
//     rotates it inflates the "Kävijät" series.
//
//   getRunId() — one UUID per WIZARD RUN, in sessionStorage. It is the *session*
//     identity the funnel actually wants. Survives a reload in the same tab (a
//     mid-wizard refresh is still one run) and dies with the tab.
//
// Why the split (fb#511): the wizard telemetry used getSessionId(), so
// COUNT(DISTINCT sessionId) in the admin funnel counted browsers, not runs — one
// person running the wizard five times counted once, and the number could only
// fall as traffic became more authenticated. That is an over-dedup, the opposite
// of the undercount originally filed.
//
// ⚠ Both ids must stay UUID v4: the ingest routes validate with UUID_RE and the
// BetonijerryAnonymousEvent.sessionId column is UNIQUEIDENTIFIER.
//
// ⚠ Every caller that sends the wizard id must send the SAME one. The backend
// back-fills the claim with `UPDATE BetonijerryAnonymousEvent ... WHERE
// sessionId = @sessionId`, so if the step events carried the run id while
// createDraft still sent the browser id, claimedByPumppuRequestId would silently
// stop being set and the funnel's `claimed` count would go to zero with nothing
// failing.

const KEY = "betonijerry.sessionId";
const RUN_KEY = "betonijerry.runId";

function uuid() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    // RFC4122 v4 polyfill
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

/** Per-BROWSER visitor id. Persistent — see the header before reusing it. */
export function getSessionId() {
    try {
        let id = localStorage.getItem(KEY);
        if (!id) {
            id = uuid();
            localStorage.setItem(KEY, id);
        }
        return id;
    } catch {
        // localStorage unavailable (private mode, ITP) — return a per-tab id
        return uuid();
    }
}

/**
 * Per-WIZARD-RUN id. sessionStorage, so it survives a reload in this tab but not
 * a new tab or a tab close — the closest thing the platform offers to "one run".
 *
 * The catch mirrors getSessionId: with storage unavailable every call mints a
 * fresh id, which over-counts rather than under-counts. That is the safer failure
 * for a funnel — a missing run is invisible, an extra one is at least visible.
 */
export function getRunId() {
    try {
        let id = sessionStorage.getItem(RUN_KEY);
        if (!id) {
            id = uuid();
            sessionStorage.setItem(RUN_KEY, id);
        }
        return id;
    } catch {
        return uuid();
    }
}
